import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Lock, Navigation, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { money, when } from "@/lib/rides";
import { PAY_LABEL } from "@/components/booking/CheckoutPanel";
import { MessagesButton } from "@/components/RideMessages";
import { ShareRideButton } from "@/components/FollowRide";
import { RideSummary, StatusBadge, Stops } from "@/components/RideStatus";
import { ModifyDialog } from "@/components/rider/ModifyDialog";
import { CLOSED_MSG, bucket, canModify, cutoffAt, dayTime, isEditableStatus, typeLabel } from "@/lib/rider";
import { Button } from "@/components/ui/button";

const payStatus = (r) => {
  if (["cancelled", "declined"].includes(r.status)) return "No charge";
  const method = r.payment_method === "ride_credit" ? `Ride Credit${r.secondary_payment_method && r.amount_due > 0 ? ` + ${PAY_LABEL[r.secondary_payment_method]}` : ""}` : PAY_LABEL[r.payment_method];
  return `${method} · ${r.payment_status === "paid" ? "Paid" : `due ${money(r.amount_due ?? r.fare)}`}`;
};

export function CutoffNote({ ride }) {
  if (!isEditableStatus(ride) || !ride.scheduled_at) return null;
  return canModify(ride) ? (
    <p className="text-[11px] text-muted-foreground" data-testid={`cutoff-open-${ride.id}`}>You can change this ride until {when(cutoffAt(ride).toISOString())} (6 hours before pickup).</p>
  ) : (
    <p className="flex items-start gap-1.5 rounded-xl bg-amber-400/10 px-3 py-2 text-[11px] text-amber-200" data-testid={`cutoff-closed-${ride.id}`}>
      <Lock className="mt-0.5 h-3 w-3 shrink-0" />{CLOSED_MSG}
    </p>
  );
}

function useCancel(ride) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/rides/${ride.id}/cancel`, {});
      toast.success("Ride cancelled");
      ["myRides", "activeRide", "mySeries", "credit", "myProfile"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, cancel };
}

function Actions({ ride, onFollow, setOpen, setEdit }) {
  const { busy, cancel } = useCancel(ride);
  const [sure, setSure] = useState(false);
  const live = ["assigned", "en_route", "arrived", "in_progress"].includes(ride.status) && ride.driver_id;
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="ghost" className="h-8 rounded-full text-xs" onClick={() => setOpen((o) => !o)} data-testid={`ride-details-${ride.id}`}>
        Details <ChevronDown className="ml-1 h-3 w-3" />
      </Button>
      {live && bucket(ride) === "active" && (
        <Button size="sm" variant="outline" className="h-8 rounded-full text-xs" onClick={onFollow} data-testid={`follow-driver-${ride.id}`}><Navigation className="mr-1 h-3 w-3" />Follow My Driver</Button>
      )}
      {!["pending", "paused", "completed", "cancelled", "declined"].includes(ride.status) && <ShareRideButton rideId={ride.id} />}
      {!["completed", "cancelled", "declined", "paused"].includes(ride.status) && <MessagesButton rideId={ride.id} className="h-8 text-xs" />}
      {canModify(ride) && (
        <Button size="sm" variant="outline" className="h-8 rounded-full text-xs" onClick={() => setEdit(true)} data-testid={`modify-ride-${ride.id}`}><Pencil className="mr-1 h-3 w-3" />Modify</Button>
      )}
      {isEditableStatus(ride) && (sure ? (
        <Button size="sm" variant="destructive" className="h-8 rounded-full text-xs" disabled={busy} onClick={cancel} data-testid={`confirm-cancel-${ride.id}`}>Yes, cancel ride</Button>
      ) : (
        <Button size="sm" variant="ghost" className="h-8 rounded-full text-xs text-muted-foreground" onClick={() => setSure(true)} data-testid={`cancel-ride-${ride.id}`}><X className="mr-1 h-3 w-3" />Cancel</Button>
      ))}
    </div>
  );
}

export function RideCard({ ride, onFollow }) {
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const { day, time } = dayTime(ride.scheduled_at);
  return (
    <article className="space-y-3 rounded-2xl border border-white/10 bg-card p-4" data-testid={`ride-card-${ride.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs text-muted-foreground" data-testid={`ride-card-date-${ride.id}`}>{day}</p>
          <p className="font-display text-xl font-bold leading-tight" data-testid={`ride-card-time-${ride.id}`}>{time}</p>
          <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-primary" data-testid={`ride-card-type-${ride.id}`}>{typeLabel(ride)}</p>
        </div>
        <StatusBadge status={ride.status} />
      </div>
      <Stops ride={ride} />
      <p className="text-xs text-muted-foreground" data-testid={`ride-card-payment-${ride.id}`}>{money(ride.fare)} · {payStatus(ride)}</p>
      <CutoffNote ride={ride} />
      <Actions ride={ride} onFollow={onFollow} setOpen={setOpen} setEdit={setEdit} />
      {open && <RideSummary ride={ride} perspective="rider" />}
      {edit && <ModifyDialog ride={ride} open={edit} onOpenChange={setEdit} />}
    </article>
  );
}
